import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CTASection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleEmailSubmit = (e) => {
    e?.preventDefault();
    if (email) {
      setIsSubmitted(true);
      // Mock API call
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    }
  };

  const quickStartSteps = [
    {
      step: '01',
      title: 'Sign Up',
      description: 'Create your free account in under 30 seconds',
      icon: 'UserPlus'
    },
    {
      step: '02',
      title: 'Get API Key',
      description: 'Receive your API credentials instantly',
      icon: 'Key'
    },
    {
      step: '03',
      title: 'Start Building',
      description: 'Send your first message in 5 minutes',
      icon: 'Rocket'
    }
  ];

  const benefits = [
    'Free tier with 10,000 messages/month',
    'No credit card required',
    'Full access to documentation',
    'Community support included',
    'Upgrade anytime as you scale'
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-background via-card/30 to-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      <div className="absolute top-20 left-20 w-32 h-32 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main CTA */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Icon name="Rocket" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              Ready to Get Started?
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
            Start building amazing
            <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              messaging experiences
            </span>
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
            Join thousands of developers who trust MessagingHub Pro to power their applications. 
            Get started in minutes with our free tier.
          </p>

          {/* Email Signup */}
          <div className="max-w-md mx-auto mb-8">
            {!isSubmitted ? (
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="Enter your work email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  required
                  className="flex-1"
                />
                <Button
                  type="submit"
                  variant="default"
                  size="default"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="whitespace-nowrap shadow-elevation-2"
                >
                  Get Started Free
                </Button>
              </form>
            ) : (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 flex items-center justify-center space-x-2">
                <Icon name="CheckCircle" size={20} className="text-accent" />
                <span className="text-accent font-medium">
                  Thanks! Check your email for next steps.
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Free forever • No credit card required • 5-minute setup
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button
              variant="outline"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => window.location.href = '/contact-sales'}
              className="border-primary/30 hover:border-primary/50 hover:bg-primary/5"
            >
              Schedule Demo
            </Button>
            <Button
              variant="ghost"
              size="lg"
              iconName="FileText"
              iconPosition="left"
              onClick={() => window.location.href = '/api-documentation'}
              className="text-muted-foreground hover:text-foreground"
            >
              View Documentation
            </Button>
          </div>

          {/* Benefits */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-muted-foreground">
            {benefits?.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Check" size={16} className="text-accent" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Start Steps */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-foreground text-center mb-12">
            Get started in 3 simple steps
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quickStartSteps?.map((step, index) => (
              <div key={index} className="text-center relative">
                {/* Step Number */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 border-2 border-primary/20 rounded-full mb-6">
                  <span className="text-2xl font-bold text-primary">{step?.step}</span>
                </div>
                
                {/* Icon */}
                <div className="flex items-center justify-center w-12 h-12 bg-card border border-border rounded-lg mx-auto mb-4">
                  <Icon name={step?.icon} size={24} className="text-foreground" />
                </div>
                
                {/* Content */}
                <h4 className="text-xl font-semibold text-foreground mb-3">
                  {step?.title}
                </h4>
                <p className="text-muted-foreground">
                  {step?.description}
                </p>
                
                {/* Connector Line */}
                {index < quickStartSteps?.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px bg-border transform -translate-x-1/2 z-0">
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
                      <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Developer Card */}
          <div className="bg-card border border-border rounded-xl p-8 hover:border-primary/30 transition-all duration-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Code" size={24} className="text-primary" />
              </div>
              <div>
                <h4 className="text-xl font-semibold text-foreground">For Developers</h4>
                <p className="text-sm text-muted-foreground">Start building immediately</p>
              </div>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Access our comprehensive documentation, SDKs, and developer tools. 
              Get your first message sent in under 5 minutes.
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={16} className="text-accent" />
                <span className="text-muted-foreground">Interactive API explorer</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={16} className="text-accent" />
                <span className="text-muted-foreground">Code examples in 10+ languages</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={16} className="text-accent" />
                <span className="text-muted-foreground">Sandbox environment</span>
              </div>
            </div>
            
            <Button
              variant="default"
              size="lg"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => window.location.href = '/developer-portal'}
            >
              Start Building
            </Button>
          </div>

          {/* Enterprise Card */}
          <div className="bg-card border border-border rounded-xl p-8 hover:border-secondary/30 transition-all duration-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="Building" size={24} className="text-secondary" />
              </div>
              <div>
                <h4 className="text-xl font-semibold text-foreground">For Enterprise</h4>
                <p className="text-sm text-muted-foreground">Custom solutions & support</p>
              </div>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Need enterprise features, custom integrations, or dedicated support? 
              Let's discuss your specific requirements.
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={16} className="text-accent" />
                <span className="text-muted-foreground">Dedicated account manager</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={16} className="text-accent" />
                <span className="text-muted-foreground">Custom SLA agreements</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={16} className="text-accent" />
                <span className="text-muted-foreground">On-premise deployment options</span>
              </div>
            </div>
            
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              iconName="MessageSquare"
              iconPosition="right"
              onClick={() => window.location.href = '/contact-sales'}
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;